/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { forwardRef, memo, type PropsWithChildren, type Ref } from 'react';

import { css, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import {
	TAB_HEIGHT,
	TAB_UNDERLINE_HEIGHT,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR,
} from '@atlassian/navigation-kit-common';

const TabWrapper = memo(
	forwardRef<
		HTMLDivElement,
		PropsWithChildren<{
			isDragging?: boolean;
			isSelected?: boolean;
		}>
	>(({ children, isSelected = false, isDragging = false }, ref: Ref<HTMLDivElement>) => {
		return (
			<div css={containerStyles}>
				<div
					css={[
						wrapperTabStyles,
						isDragging && draggingTabStyles,
						isSelected && selectedWrapperTabStyles,
						fg('blu-7829_tab_nav_active_area_fix') && activeAreaPaddingStyles,
					]}
					ref={ref}
				>
					{children}
				</div>
			</div>
		);
	}),
);

const wrapperTabStyles = css({
	paddingBlock: token('space.050'),
	paddingLeft: token('space.075'),
	paddingRight: token('space.075'),
	display: 'flex',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	blockSize: `${TAB_HEIGHT}px`,
	boxSizing: 'border-box',
	minInlineSize: 'max-content',
	alignItems: 'center',
	flexDirection: 'row-reverse',
	outlineOffset: token('space.negative.025'),
	borderRadius: token('radius.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:has([data-delegated-focus-ring]:focus-visible)': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:hover': {
		'&::after': {
			position: 'absolute',
			left: token('space.075', '6px'),
			right: token('space.075', '6px'),
			bottom: token('space.0', '0px'),
			top: 'auto',
			content: "''",
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			height: `${TAB_UNDERLINE_HEIGHT}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
			zIndex: -1,
		},
	},
});

const selectedWrapperTabStyles = css({
	'&::after': {
		position: 'absolute',
		left: token('space.075', '6px'),
		right: token('space.075', '6px'),
		bottom: token('space.0', '0px'),
		top: 'auto',
		content: "''",
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		height: `${TAB_UNDERLINE_HEIGHT}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
		zIndex: -1,
	},
});

// Padding on wrapper reduces active area of the tab, leaving a gap between hover styles
const activeAreaPaddingStyles = css({
	paddingLeft: token('space.0'),
	paddingRight: token('space.0'),
});

/*
	This container is used to ensure that the outline is above the tab underline by creating a new stacking context
	around the tab wrapper (which has the ::after pseudo-element inside it).
*/
const containerStyles = css({
	position: 'relative',
	zIndex: 1,
});

const draggingTabStyles = css({
	opacity: 0.1,
});

export default TabWrapper;
